import { ToastService } from 'src/app/base/services/toast.service';
import { FieldAssignmentGridComponent } from './../shared/field-assignment-grid/field-assignment-grid.component';
import { ImportConfigurationFileFormType } from './../configuration-form-file-form-type';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ImportFileOptionsDTO, ImportSourceFieldDefinitionDTO, SampleFileDTO } from './../../../../../../base/models/models.swagger.generated';
import { DialogComponent, DialogModule, TooltipModule } from '@syncfusion/ej2-angular-popups';
import { SelectedEventArgs, FileInfo, Uploader, UploaderModule } from '@syncfusion/ej2-angular-inputs';
import { ImportConfigurationEditFormsCoordinatorService } from '../../import-configuration-edit-forms-coordinator.service';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MimeTypeHelper } from 'src/app/base/shared/mime-type.helper';
import { FileFormatHelper } from 'src/app/base/shared/file-format.helper';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FieldAssignmentGridComponent as FieldAssignmentGridComponent_1 } from '../shared/field-assignment-grid/field-assignment-grid.component';
import { QueryBuilderModule } from '@syncfusion/ej2-angular-querybuilder';
import { ToolbarModule } from '@syncfusion/ej2-angular-navigations';
import { CommandModel, EditSettingsModel, GridComponent, GridModule, NotifyArgs, SaveEventArgs } from '@syncfusion/ej2-angular-grids';
import { SourceEditFormComponent } from './source-edit-form/source-edit-form.component';
import { SourceEditFormType } from './source-edit-form/source-edit-form-type';
import { FileColumnBasedService } from './file-column-based.service';

@Component({
    selector: 'app-file-column-based',
    templateUrl: './file-column-based.component.html',
    styleUrls: ['./file-column-based.component.scss'],
    imports: [GridModule, ToolbarModule, QueryBuilderModule, FieldAssignmentGridComponent_1, DialogModule, UploaderModule, TranslateModule, SourceEditFormComponent, TooltipModule]
})
export class FileColumnBasedComponent implements OnInit {

  @ViewChild('dialog') public dialog: DialogComponent;
  @ViewChild('defaultupload') public defaultupload: Uploader;
  @ViewChild('fieldAssignmentGrid') public fieldAssignmentGrid: FieldAssignmentGridComponent;
  @ViewChild('grid') public grid: GridComponent;

  public allowedExtensions: string;

  public sourceEditForm: FormGroup<SourceEditFormType>;

  public editSettings?: EditSettingsModel = {
    allowEditing: true, allowAdding: true, allowDeleting: true, mode: 'Dialog',
    showDeleteConfirmDialog: true
  };

  public sourceFieldGridCommands = [{
    type: 'Edit',
    title: this.translateService.instant('i18n.domain.import-configuration.edit.file.column-based.edit-source'),
    buttonOption:
    {
      cssClass: 'icon-btn',
      iconCss: 'icon-edit e-icons'
    }
  } as CommandModel];


  public get configurationFormFile(): FormGroup<ImportConfigurationFileFormType> {
    return this.formsCoordinationService.configurationFormFile;
  }

  public selectedSource: ImportSourceFieldDefinitionDTO = new ImportSourceFieldDefinitionDTO();

  constructor(
    public formsCoordinationService: ImportConfigurationEditFormsCoordinatorService,
    public fileColumnBasedService: FileColumnBasedService,
    public toastService: ToastService,
    public translateService: TranslateService,
    private fb: FormBuilder
  ) { }

  public ngOnInit(): void {
    this.initializeSourceEditForm(new ImportSourceFieldDefinitionDTO());
  }

  public openUploader() {
    this.allowedExtensions = FileFormatHelper.getAllowedFileExtensionsForCorrectionFiles(this.formsCoordinationService.configurationFormFile.controls.fileFormat.value);
    this.dialog.show();
  }

  public closeUploader(): void {
    this.dialog.hide();
  }

  public getColumnDisplayName(data: ImportSourceFieldDefinitionDTO): string {
    if (!data) {
      return '';
    }
    if (data.sampleValue) {
      return data.sourceElementName + ' (' + data.sampleValue + ')';
    }
    return data.sourceElementName;
  }

  public handleFileInput($event: SelectedEventArgs) {
    const files: FileInfo[] = $event.filesData;
    if (!files || files.length < 1) {
      return;
    }
    const file = files[0];
    const fileEndings = this.allowedExtensions.replace('.', '').split(',');
    if (!fileEndings.some((ending) => file.type.toLowerCase() === ending)) {
      this.toastService.showError('i18n.domain.import-configuration.edit.file.column-based.file-type-invalid', null);
      setTimeout(() => {
        this.defaultupload.clearAll();
      }, 0);
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      let base64Data = event.target.result as string;
      const startIndex = base64Data.search(';base64,');
      if (startIndex > 0) {
        // replace type by cert
        base64Data = 'data:' + MimeTypeHelper.getMimeType(file.type.toLowerCase()) + base64Data.substring(startIndex);
      } else {
        base64Data = 'data:' + MimeTypeHelper.getMimeType(file.type.toLowerCase()) + ';base64,' + base64Data;
      }
      const dto = new SampleFileDTO({
        fileContentBase64: base64Data,
        fileOptions: new ImportFileOptionsDTO({
          separatorCharacter: this.formsCoordinationService.configurationFormFile.controls.seperatorCharacter.value,
          hasTitle: this.formsCoordinationService.configurationFormFile.controls.isTitleContainedInFile.value,
          textFormat: this.formsCoordinationService.configurationFormFile.controls.textFormat.value,
        }),
      });
      this.fileColumnBasedService.getPossibleSourcesFromFile(dto).subscribe((sourceFieldDefinitions) => {
        this.formsCoordinationService.configurationFormFile.controls.possibleSources.setValue(sourceFieldDefinitions);
        //Reset all field assignments
        this.formsCoordinationService.configurationFormFile.controls.fieldAssignments.value.forEach((fieldAssignment) => {
          fieldAssignment.sourceElementName = null;
          fieldAssignment.sourceElementSelectionPath = null;
        });
        this.formsCoordinationService.configurationFormFile.controls.fieldAssignments.updateValueAndValidity();
        this.fieldAssignmentGrid.grid.refresh();
        this.selectedSource = null;
      });
      this.dialog.hide();
      this.defaultupload.clearAll();
    };
    reader.readAsDataURL(file.rawFile as File);
  }

  public onFileRemove() {
    this.defaultupload?.refresh(); // get rid of added input fields
    this.defaultupload?.clearAll();
    this.formsCoordinationService.configurationFormFile.controls.possibleSources.setValue([]);
    this.selectedSource = null;
  }

  public sourceGridActionBegin(args: SaveEventArgs) {
    if (args.requestType === 'beginEdit' || args.requestType === 'add') {
      this.initializeSourceEditForm(args.rowData);
    }
    else if (args.requestType === 'save') {
      if (this.sourceEditForm.valid) {
        var dto = new ImportSourceFieldDefinitionDTO();
        dto.sourceElementName = this.sourceEditForm.value.sourceElementName;
        dto.sourceElementSelectionPath = this.sourceEditForm.value.sourceElementSelectionPath;
        args.data = dto;
      } else {
        args.cancel = true;
      }
    }
  }

  public sourceGridActionComplete(args) {
    if ((args.requestType === 'beginEdit' || args.requestType === 'add')) {
      const dialog = (args as any).dialog;
      dialog.showCloseIcon = false;
      dialog.height = 280;
      dialog.width = 600;
      // change the header of the dialog
      dialog.header = args.requestType === 'beginEdit'
        ? this.translateService.instant('i18n.domain.import-configuration.edit.file.column-based.edit-source') + ' ' + (args.rowData as ImportSourceFieldDefinitionDTO)['sourceElementName']
        : this.translateService.instant('i18n.domain.import-configuration.edit.file.column-based.add-source');
    }
    if (args.requestType === 'save') {
      if (args.action === 'add') {
        const argz = args as NotifyArgs;
        const index = argz.rows?.findIndex((row) => this.addObjectCompare(args.data, row.data));
        if (index < 0) {
          this.grid?.clearRowSelection();
        } else {
          this.grid?.selectRow(index);
        }
      }
    }
  }

  private addObjectCompare(data: any, data1: Object): unknown {
    return data['sourceElementName'] === data1['sourceElementName']
      && data['sourceElementSelectionPath'] === data1['sourceElementSelectionPath'];
  }

  public initializeSourceEditForm(data: any): void {
    this.sourceEditForm = this.fb.group<SourceEditFormType>({
      sourceElementName: this.fb.control<string>({ value: data.sourceElementName ?? null, disabled: false }, [Validators.required]),
      sourceElementSelectionPath: this.fb.control<string>({ value: data.sourceElementSelectionPath ?? null, disabled: false }, [Validators.required])
    });
  }

}
